import { z } from "zod"

const timeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/)

export const pushScheduleSchema = z.object({
	enabled: z.boolean(),
	/** 0 = Sunday, as Date#getDay. */
	days: z.array(z.number().int().min(0).max(6)).max(7),
	from: timeSchema,
	to: timeSchema,
	/** IANA zone from the phone that saved it. */
	timeZone: z.string().min(1).max(64),
})

export type PushSchedule = z.infer<typeof pushScheduleSchema>

export const APNS_ENVIRONMENTS = ["sandbox", "production"] as const
export type ApnsEnvironment = (typeof APNS_ENVIRONMENTS)[number]

export const pushSettingsSchema = z.object({
	/** Stored only when off: push is on by default once the phone allows it. */
	enabled: z.boolean().optional(),
	schedule: pushScheduleSchema.optional(),
})

export type PushSettings = z.infer<typeof pushSettingsSchema>

export const DEFAULT_PUSH_SCHEDULE: Omit<PushSchedule, "timeZone"> = {
	enabled: false,
	days: [1, 2, 3, 4, 5],
	from: "09:00",
	to: "18:00",
}

/** Tolerant read: a bad schedule is dropped, never the whole block. */
export function parsePushSettings(raw: unknown): PushSettings | undefined {
	if (!raw || typeof raw !== "object") return undefined
	const value = raw as Record<string, unknown>
	const push: PushSettings = {}
	if (value.enabled === false) push.enabled = false
	const schedule = pushScheduleSchema.safeParse(value.schedule)
	if (schedule.success) {
		push.schedule = {
			...schedule.data,
			days: [...new Set(schedule.data.days)].sort(),
		}
	}
	return compactPush(push)
}

export function compactPush(push: PushSettings): PushSettings | undefined {
	const next: PushSettings = {}
	if (push.enabled === false) next.enabled = false
	if (push.schedule) next.schedule = push.schedule
	return Object.keys(next).length > 0 ? next : undefined
}

function minutesOf(time: string): number {
	const [h, m] = time.split(":").map(Number)
	return (h ?? 0) * 60 + (m ?? 0)
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

function localClock(at: Date, timeZone: string) {
	const parts = new Intl.DateTimeFormat("en-US", {
		timeZone,
		weekday: "short",
		hour: "2-digit",
		minute: "2-digit",
		hourCycle: "h23",
	}).formatToParts(at)
	const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ""
	return {
		day: WEEKDAYS.indexOf(get("weekday")),
		minutes: Number(get("hour")) * 60 + Number(get("minute")),
	}
}

/**
 * True when a push may go out at `at`. A window that crosses midnight
 * (22:00 to 06:00) belongs to the day it starts on. A zone the runtime
 * doesn't know fails open, since the notification still sits in the inbox.
 */
export function isWithinPushSchedule(
	schedule: PushSchedule | undefined,
	at: Date,
): boolean {
	if (!schedule?.enabled) return true
	let clock: { day: number; minutes: number }
	try {
		clock = localClock(at, schedule.timeZone)
	} catch {
		return true
	}
	if (clock.day < 0) return true
	const from = minutesOf(schedule.from)
	const to = minutesOf(schedule.to)
	const days = new Set(schedule.days)
	if (from === to) return days.has(clock.day)
	if (from < to) {
		return days.has(clock.day) && clock.minutes >= from && clock.minutes < to
	}
	if (clock.minutes >= from) return days.has(clock.day)
	if (clock.minutes < to) return days.has((clock.day + 6) % 7)
	return false
}
